import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import {
  DeleteOutlined,
  MoreVertOutlined,
  VerticalAlignBottomOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import type { SignedUrl } from "@/graphql/codegen/graphql"
import { useFileDownload } from "@/hooks/useFileDownload"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface InvoiceDocumentRowActionsProps {
  row: CellContext<SignedUrl, unknown>["row"]
  onDelete?: (document: SignedUrl) => void
}

export const InvoiceDocumentRowActions: FC<InvoiceDocumentRowActionsProps> = ({ row, onDelete }) => {
  const { downloadFile } = useFileDownload()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const handleViewDocument = () => {
    handleMenuClose()
    if (row.original.url) window.open(row.original.url, "_blank", "noopener,noreferrer")
  }

  const handleDownloadDocument = async () => {
    handleMenuClose()
    const { url, file } = row.original
    if (!file?.originalName) {
      notify({ type: NotificationType.Error, message: "Unable to download this document" })
      return
    }
    await downloadFile(url, file.originalName)
  }

  const handleDeleteDocument = () => {
    handleMenuClose()
    onDelete?.(row.original)
  }

  const fileId = row.original.file?.fileId ?? ""

  return (
    <Box className='flex justify-end'>
      <IconButton
        ref={anchorElRef}
        size='small'
        onClick={handleMenuOpen}
        data-testid={`invoices.document-row-actions.kebab-button-${fileId}`}
      >
        <MoreVertOutlined />
      </IconButton>
      <Menu anchorEl={anchorElRef.current} open={isMenuOpen} onClose={handleMenuClose}>
        <MenuItem
          onClick={handleViewDocument}
          data-testid={`invoices.document-row-actions.view-document-menu-item-${fileId}`}
        >
          <ListItemIcon>
            <VisibilityOutlined />
          </ListItemIcon>
          <ListItemText primary='View Document' />
        </MenuItem>
        <MenuItem
          onClick={handleDownloadDocument}
          data-testid={`invoices.document-row-actions.download-document-menu-item-${fileId}`}
        >
          <ListItemIcon>
            <VerticalAlignBottomOutlined />
          </ListItemIcon>
          <ListItemText primary='Download Document' />
        </MenuItem>
        {onDelete && (
          <MenuItem
            onClick={handleDeleteDocument}
            data-testid={`invoices.document-row-actions.delete-document-menu-item-${fileId}`}
          >
            <ListItemIcon>
              <DeleteOutlined />
            </ListItemIcon>
            <ListItemText primary='Delete Document' />
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
